import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Button, Dropdown, Grid, Tag } from 'antd';

import TagBadge from './TagBadge';
import TagEditModal from './TagEditModal';
import TagRefusal from './TagRefusal';
import TagMergeModal from './TagMergeModal';
import TagMoveModal from './TagMoveModal';
import confirmTagDelete from './confirmTagDelete';
import TagRuleEditor from './TagRuleEditor';
import TagRulePass, { readFullItems } from './TagRulePass';
import { confirmTagRuleStop } from './confirmTagRule';
import { stopTagRule } from '../../services/tags';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { isAutomaticTag, isPausedTag, ruleBlockedReason, tagRuleRefusal, unfinishedStop } from '../../utils/tagRules';
import GroupHeader from '../common/GroupHeader';
import TabLoadState, { RefreshFailedNotice } from '../common/TabLoadState';
import { Skeleton } from '../loading';
import SortableList, { ReorderButton } from '../common/SortableList';
import { buildActionMenu } from '../common/actionMenu';
import { ARROW_DOWN, ARROW_UP, AUTOMATION, DIAGRAM_2, FLOWCHART as MERGE_ICON, KEBAB, PLUS } from '../icons';
import { formatCount } from '../../utils/counts';
import { cleanupSummary } from '../../utils/itemTagSettings';
import { formatRelativeToNow } from '../../utils/formatters';
import {
	TAG_LIMITS,
	applyToNewItemsOf,
	canAddUnder,
	childrenOf,
	hasChildren,
	isRestrictedTag,
	joinWords,
	placementForDrop,
	placementForStep,
	positionAfter,
	renumberedPositions,
	tagIdOf,
	tagPagePath,
	tagParentIdOf,
	treeOrder,
	withTag,
	withTagPatched,
	withoutTag,
} from '../../utils/tags';

/**
 * THE TAG MANAGER — one component for both vocabularies (docs/plans/tags.md
 * §5.2): Settings › Tags (a collection's tags, its managers) and Account ›
 * Tags (a person's private tags). The tree, and everything a manager does to
 * it, applying at once through the tag endpoints (services/tags.js):
 *
 * - **Add tag**, and **Add a tag under it** from a row's ⋯ (three levels at
 *   most, ruled d04).
 * - **Edit…** — name, icon and color, description, Restricted (collection
 *   tags only).
 * - **The tree order.** A manager asks for it (**Reorder**, like every
 *   sortable list — docs/platform-standards.md § Drag to reorder) and drags
 *   a grip: a dropped tag takes the place of the row it lands above, under
 *   that row's parent (utils/tags.js placementForDrop). In reorder mode each
 *   row's ⋯ also has **Move up** / **Move down**. **Move to…** is always in
 *   the ⋯ — the way to put a tag under any other, and the way on a phone.
 *   Optimistic, with rollback: a refused move puts the tree back and says why.
 * - **Merge into…** (T9) — folds the tag into another of the same owner
 *   (TagMergeModal: pick, then confirm with the counts a dry run returns). A
 *   merge deletes the tag for good, so it sits in the Danger group, before
 *   Delete…; restricted and automatic tags can't be merged (said on the row).
 * - **Delete…** — confirmed after THE IMPACT REPORT (confirmTagDelete, T4d:
 *   what refers to the tag, each with Remove for me / Leave it; the choices
 *   ride the delete as `cleanup`). Refused while tags sit under it.
 * - **Deleted tags** — the tags that may be restored, each with **Restore**.
 * - **Apply by a rule…** (T6a, collection tags, when `rules` is given; the
 *   verb ruled b62 — the tag's own label and mark stay "Applied by a rule") —
 *   the rule's editor (TagRuleEditor); an automatic tag wears the
 *   `automation` glyph in the tree — and **Paused** beside it while its rule
 *   is paused (`rule.enabled` false, keepr-api T6b) — and **Stop applying by
 *   rule…** joins the Danger group (a dry run's counts and the items that
 *   lose the tag, named, confirmed). The pass a rule's save, pause or stop
 *   starts is followed on the tag's row (TagRulePass).
 *
 * - **Apply to new items of** (T7, ruled d22; collection tags, when
 *   `newItemCards` is given) — in a restricted tag's Edit…, the cards whose
 *   new items get the tag as they are made; the row says them in a quiet
 *   line under the badge ("Goes on new items of Visit and Note as they’re
 *   made").
 *
 * Every refusal is said plainly, in the API's own sentence (utils/tags.js
 * tagRefusal): a name taken beside it, 500 tags, tags under it, a restricted
 * tag in use, an archived collection. A restricted tag that access settings
 * name (T7) says which — each a link to where it is changed
 * (components/tags/TagRefusal.js).
 *
 * Props:
 *   tags         the owner's own tags (tree order; the list the caller holds)
 *   loading, error, onRetry  the read the list came from: a wait is the standard
 *                skeleton, a failure an error with Retry (never an empty list)
 *   canManage    may change them (a manager of the collection; always for
 *                private tags). Without it the tree is read-only.
 *   readOnlyNote why not, when the collection is archived
 *   allowRestricted  collection tags (the Restricted switch)
 *   privateTags  the private vocabulary (its words: "Only you see this")
 *   service      { create(fields), update(id, patch), check?(id, patch), remove(id, { cleanup }?), restore(id), listDeleted(), merge?(id, into, { dryRun }) }
 *                — `check` is the change's dry run (`{ tag, warnings }`,
 *                services/tags.js checkTagUpdate): the tag dialog asks it
 *                before "Apply to new items of" gains a card (b75)
 *   onReplace(list)  the optimistic write: the caller's list becomes `list`
 *   onChanged()  after every successful write (a caller's other caches)
 *   addButtonType 'primary' where this is the page's one primary (Account)
 *   emptyText    what the empty tree says
 *   rules        `{ collection }` — automatic tags are offered (Settings ›
 *                Tags); null for private tags, which never have a rule (d10)
 *   openRuleTagId  a tag whose rule editor opens once it is in the tree
 *                (the "Why" pop-over's Edit rule door, `?rule=`)
 *   extraActions(tag)  more ⋯ entries for a row, in the action taxonomy's
 *                shape — the caller's own verbs (Account › Private tags: Put
 *                on collections…, b53; Make active… / Stop adding it, the
 *                active tag, b52); each lands in its group
 *   newItemCards the cards "Apply to new items of" offers (T7): the
 *                collection's and its sub-collections', as card options
 *                (`[{ value, card, depth, searchText }]`); null for private
 *                tags, which are never restricted
 */
export default function TagManager({
	tags = [],
	loading = false,
	error = null,
	onRetry,
	canManage = true,
	readOnlyNote = null,
	allowRestricted = false,
	privateTags = false,
	service,
	onReplace,
	onChanged,
	addButtonType = 'default',
	emptyText = 'No tags yet. Add one to start.',
	label = 'Tags',
	rules = null,
	openRuleTagId = null,
	extraActions = null,
	newItemCards = null,
}) {
	const { message, modal } = App.useApp();
	const cardDefinitions = useOptionalCardDefinitions();
	const screens = Grid.useBreakpoint();
	const phone = screens.md === false;
	const ordered = useMemo(() => treeOrder(tags), [tags]);
	const [reordering, setReordering] = useState(false);
	const [editing, setEditing] = useState(null); // { tag } | { parentId } (new)
	const [moving, setMoving] = useState(null); // the tag
	const [merging, setMerging] = useState(null); // the tag
	const [refusal, setRefusal] = useState(null);
	const [deletedOpen, setDeletedOpen] = useState(false);
	const [deleted, setDeleted] = useState({ loaded: false, loading: false, tags: [], error: null });
	const writable = canManage && !readOnlyNote;
	const [ruleFor, setRuleFor] = useState(null); // the tag whose rule editor is open
	const [passes, setPasses] = useState({}); // tag id → the pass its last rule act started
	const [leftovers, setLeftovers] = useState({}); // tag id → items still carrying a hand tag by rule
	const rulesOn = Boolean(rules) && !privateTags;

	const changed = useCallback(() => { if (typeof onChanged === 'function') onChanged(); }, [onChanged]);

	const loadDeleted = useCallback(async () => {
		setDeleted(prev => ({ ...prev, loading: true, error: null }));
		try {
			const list = await service.listDeleted();
			setDeleted({ loaded: true, loading: false, tags: Array.isArray(list) ? list : [], error: null });
		} catch (err) {
			setDeleted(prev => ({ ...prev, loading: false, error: err }));
		}
	}, [service]);

	const toggleDeleted = () => {
		const next = !deletedOpen;
		setDeletedOpen(next);
		if (next) loadDeleted();
	};

	// ---- writes ----------------------------------------------------------

	const save = async (fields, { tag }) => {
		if (tag) {
			let saved;
			try {
				saved = await service.update(tagIdOf(tag), fields);
			} catch (err) {
				// T6a: items still carry the tag by rule ("Stop applying by rule"
				// has not finished): its ⋯ offers the stop again.
				const data = err?.response?.data || {};
				if (data.code === 'applied_by_rule') setLeftovers(prev => ({ ...prev, [tagIdOf(tag)]: Number(data.count) || 1 }));
				throw err;
			}
			onReplace(withTag(tags, saved || { ...tag, ...fields }));
			message.success(`Saved “${(saved || tag).name}”.`);
		} else {
			const saved = await service.create(fields);
			if (saved) onReplace(withTag(tags, saved));
			message.success(`Added “${saved?.name || fields.name}”.`);
		}
		setRefusal(null);
		changed();
	};

	/**
	 * Puts `id` under `parentId` at `position` — or renumbers a set of
	 * siblings when there is no room between two (`renumber`: their ids in
	 * the new order). Optimistic: the tree moves at once, and a refusal puts
	 * it back and says why. `throwOnError` hands a refusal to a dialog.
	 */
	const place = async (id, placement, { throwOnError = false } = {}) => {
		if (!placement) return;
		if (placement.refused) {
			message.warning(placement.refused);
			return;
		}
		const before = tags;
		const tag = tags.find(t => tagIdOf(t) === id);
		const parentChanged = (placement.parentId || null) !== tagParentIdOf(tag);
		let steps;
		if (placement.renumber) {
			const moves = renumberedPositions(tags, placement.renumber);
			steps = moves.map(({ id: tid, position }) => ({ id: tid, patch: { position, ...(tid === id && parentChanged ? { parentId: placement.parentId || null } : {}) } }));
			if (parentChanged && !steps.some(step => step.id === id)) steps.push({ id, patch: { parentId: placement.parentId || null } });
		} else {
			steps = [{ id, patch: { position: placement.position, ...(parentChanged ? { parentId: placement.parentId || null } : {}) } }];
		}
		let draft = tags;
		steps.forEach(step => { draft = withTagPatched(draft, step.id, step.patch); });
		onReplace(draft);
		try {
			let latest = draft;
			for (const step of steps) {
				const saved = await service.update(step.id, step.patch); // eslint-disable-line no-await-in-loop
				if (saved) latest = withTag(latest, saved);
			}
			onReplace(latest);
			setRefusal(null);
			changed();
		} catch (err) {
			onReplace(before);
			if (throwOnError) throw err;
			const status = err?.response?.status;
			setRefusal(status && status < 500
				? <TagRefusal lead={`“${tag?.name}” did not move.`} error={err} tags={tags} />
				: 'Could not save that order. Please try again.');
		}
	};

	const handleReorder = (ids, activeId) => place(activeId, placementForDrop(tags, ids, activeId));

	const moveTo = parentId => {
		const tag = moving;
		const siblings = childrenOf(tags, parentId).filter(t => tagIdOf(t) !== tagIdOf(tag));
		return place(tagIdOf(tag), { parentId, position: positionAfter(siblings) }, { throwOnError: true });
	};

	const remove = async tag => {
		// The impact report first (T4d): what refers to the tag, and the
		// "Remove for me" choices that ride the delete.
		const decision = await confirmTagDelete(modal, tag);
		if (!decision) return;
		const cleanup = Array.isArray(decision.cleanup) ? decision.cleanup : [];
		try {
			const result = cleanup.length ? await service.remove(tagIdOf(tag), { cleanup }) : await service.remove(tagIdOf(tag));
			onReplace(withoutTag(tags, tagIdOf(tag)));
			const items = result?.applications?.items;
			const { done, failed } = cleanupSummary(result?.cleanup);
			message.success([
				typeof items === 'number' && items > 0
					? `Deleted “${tag.name}”. It came off ${formatCount(items, 'item')}.`
					: `Deleted “${tag.name}”.`,
				done,
			].filter(Boolean).join(' '));
			if (failed.length) {
				message.warning(`${formatCount(failed.length, 'reference')} could not be removed for you. ${failed.length === 1 ? 'It stays' : 'They stay'}, and stop${failed.length === 1 ? 's' : ''} matching.`);
			}
			setRefusal(null);
			if (deletedOpen) loadDeleted();
			changed();
		} catch (err) {
			setRefusal(<TagRefusal lead={`“${tag.name}” was not deleted.`} error={err} fallback="Please try again." />);
		}
	};

	/** A merge landed: the tag is gone, the survivor is as the API returned it (its aliases, maybe). */
	const merged = ({ tag: survivor, merge } = {}) => {
		const tag = merging;
		const kids = childrenOf(tags, tagIdOf(tag));
		let next = withoutTag(tags, tagIdOf(tag));
		if (survivor) next = withTag(next, survivor);
		// The tags under it moved under the survivor (the API put them after its own).
		kids.forEach(kid => { next = withTagPatched(next, tagIdOf(kid), { parentId: tagIdOf(survivor) || (merge?.into?._id ?? null) }); });
		onReplace(next);
		const items = merge?.private ? (merge.private.items || 0) + (merge.private.collections || 0) : merge?.items;
		message.success(items
			? `Merged “${tag.name}” into “${survivor?.name || merge?.into?.name}”. It moved to ${formatCount(items, merge?.private ? 'item or collection' : 'item', merge?.private ? 'items and collections' : 'items')}.`
			: `Merged “${tag.name}” into “${survivor?.name || merge?.into?.name}”.`);
		setRefusal(null);
		changed();
	};

	const restore = async tag => {
		try {
			const { tag: back, applications } = await service.restore(tagIdOf(tag));
			if (back) onReplace(withTag(tags, back));
			setDeleted(prev => ({ ...prev, tags: prev.tags.filter(t => tagIdOf(t) !== tagIdOf(tag)) }));
			const restored = applications?.restored;
			message.success(typeof restored === 'number' && restored > 0
				? `Restored “${tag.name}”. It went back on ${formatCount(restored, 'item')}.`
				: `Restored “${tag.name}”.`);
			setRefusal(null);
			changed();
		} catch (err) {
			setRefusal(<TagRefusal lead={`“${tag.name}” was not restored.`} error={err} fallback="Please try again." />);
		}
	};

	// ---- automatic tags (T6a) ---------------------------------------------

	const trackPass = useCallback((tagId, pass) => {
		if (tagId && pass) setPasses(prev => ({ ...prev, [tagId]: pass }));
	}, []);
	const dropPass = tagId => setPasses(prev => { const next = { ...prev }; delete next[tagId]; return next; });

	/** "Stop applying by rule": the dry run's counts, confirmed, then the stop. → true when it stopped. */
	const stopRule = async (tag, { report } = {}) => {
		const say = typeof report === 'function' ? report : text => setRefusal(text);
		const tagId = tagIdOf(tag);
		// A hand tag whose automatic applications are still there (a stop
		// that has not finished): the same call finishes it (the API's heal).
		const finishing = !isAutomaticTag(tag);
		let dry;
		try {
			dry = await stopTagRule(tagId, { dryRun: true });
		} catch (err) {
			say(`“${tag.name}” is still applied by its rule. ${tagRuleRefusal(err)}`);
			return false;
		}
		if (finishing && !(Number(dry?.movesToHand) > 0)) {
			setLeftovers(prev => { const next = { ...prev }; delete next[tagId]; return next; });
			message.info(`No item carries “${tag.name}” by rule any more.`);
			return false;
		}
		// The items at 50 that lose the tag, named before the person decides
		// (the dry run's `fullIds`, T6b), the way the pass's report names them.
		const fullItems = Number(dry?.full) > 0 ? await readFullItems(dry?.fullIds) : [];
		const ok = await confirmTagRuleStop(modal, {
			tagName: tag.name,
			movesToHand: dry?.movesToHand,
			full: dry?.full,
			finishing,
			fullItems,
			cardsById: cardDefinitions?.cardsById || {},
		});
		if (!ok) return false;
		try {
			const result = await stopTagRule(tagId);
			onReplace(withTag(tags, result?.payload || { ...tag, rule: null }));
			trackPass(tagId, result?.pass);
			setLeftovers(prev => { const next = { ...prev }; delete next[tagId]; return next; });
			message.success(finishing ? `Turning the rest of “${tag.name}”’s automatic applications into hand ones.` : `“${tag.name}” is no longer applied by a rule.`);
			setRefusal(null);
			changed();
			return true;
		} catch (err) {
			say(`“${tag.name}” is still applied by its rule. ${tagRuleRefusal(err)}`);
			return false;
		}
	};

	// Paused or resumed in the rule's editor: the tree's Paused follows.
	const ruleSwitched = (tagId, enabled) => {
		const current = tags.find(t => tagIdOf(t) === tagId);
		if (!current?.rule) return;
		onReplace(withTagPatched(tags, tagId, { rule: { ...current.rule, enabled: enabled !== false } }));
		changed();
	};

	const ruleSaved = result => {
		const saved = result?.payload;
		if (saved) onReplace(withTag(tags, saved));
		trackPass(tagIdOf(saved || ruleFor), result?.pass);
		changed();
	};

	// The "Why" pop-over's Edit rule opens the editor once the tag is here.
	const openedRuleFor = useRef(null);
	useEffect(() => {
		if (!rulesOn || !openRuleTagId || !writable || openedRuleFor.current === openRuleTagId) return;
		const target = tags.find(t => tagIdOf(t) === openRuleTagId);
		if (!target || ruleBlockedReason(target)) return;
		openedRuleFor.current = openRuleTagId;
		setRuleFor(target);
	}, [rulesOn, openRuleTagId, writable, tags]);

	// ---- the rows --------------------------------------------------------

	// "Apply to new items of" (T7), said on a restricted tag's row: the cards
	// by name — the card options', else the cache's — "a card" for one
	// neither can name.
	const cardNameOf = id => (newItemCards || []).find(option => option.value === id)?.card?.name
		|| cardDefinitions?.cardsById?.[id]?.name
		|| null;
	const newItemsLine = tag => {
		if (!newItemCards || !isRestrictedTag(tag)) return null;
		const ids = applyToNewItemsOf(tag);
		if (!ids.length) return null;
		const known = ids.map(cardNameOf).filter(Boolean);
		const unknown = ids.length - known.length;
		const others = known.length ? (unknown === 1 ? 'one other card' : `${unknown} other cards`) : (unknown === 1 ? 'a card' : `${unknown} cards`);
		const names = unknown ? [...known, others] : known;
		return `Goes on new items of ${joinWords(names)} as they’re made`;
	};

	const rowActions = tag => {
		const id = tagIdOf(tag);
		const restricted = isRestrictedTag(tag);
		const addUnder = canAddUnder(tags, id);
		const children = hasChildren(tags, id);
		const siblings = childrenOf(tags, tagParentIdOf(tag));
		const at = siblings.findIndex(t => tagIdOf(t) === id);
		return [
			{ key: 'edit', group: 'record', glyph: 'edit', label: 'Edit…', onClick: () => setEditing({ tag }) },
			{
				key: 'add-under',
				group: 'record',
				icon: <PLUS />,
				label: 'Add a tag under it',
				disabled: !addUnder.ok,
				title: addUnder.ok ? undefined : addUnder.reason,
				onClick: () => setEditing({ parentId: id }),
			},
			{
				key: 'move',
				group: 'record',
				icon: <DIAGRAM_2 />,
				label: 'Move to…',
				disabled: restricted,
				title: restricted ? 'A restricted tag stands alone: it cannot sit under another tag.' : undefined,
				onClick: () => setMoving(tag),
			},
			...(rulesOn ? [{
				key: 'rule',
				group: 'record',
				icon: <AUTOMATION />,
				label: 'Apply by a rule…',
				disabled: Boolean(ruleBlockedReason(tag)),
				title: ruleBlockedReason(tag) || undefined,
				onClick: () => setRuleFor(tag),
			}] : []),
			...(typeof extraActions === 'function' ? (extraActions(tag) || []).filter(Boolean) : []),
			...(reordering ? [
				{ key: 'up', group: 'record', icon: <ARROW_UP />, label: 'Move up', disabled: at <= 0, onClick: () => place(id, placementForStep(tags, id, -1)) },
				{ key: 'down', group: 'record', icon: <ARROW_DOWN />, label: 'Move down', disabled: at < 0 || at >= siblings.length - 1, onClick: () => place(id, placementForStep(tags, id, 1)) },
			] : []),
			...(typeof service?.merge === 'function' ? [{
				key: 'merge',
				group: 'destructive',
				icon: <MERGE_ICON />,
				label: 'Merge into…',
				disabled: restricted || isAutomaticTag(tag) || tags.length < 2,
				title: restricted
					? 'Restricted tags can’t be merged: they decide who can see what.'
					: isAutomaticTag(tag)
						? 'An automatic tag can’t be merged. Stop applying it by rule first.'
						: (tags.length < 2 ? 'There is no other tag to merge it into.' : undefined),
				onClick: () => setMerging(tag),
			}] : []),
			...(rulesOn && (isAutomaticTag(tag) || leftovers[id] || unfinishedStop(passes[id])) ? [{
				key: 'stop-rule',
				group: 'destructive',
				glyph: 'remove',
				label: 'Stop applying by rule…',
				onClick: () => stopRule(tag),
			}] : []),
			{
				key: 'delete',
				group: 'destructive',
				glyph: 'delete',
				label: 'Delete…',
				disabled: children,
				title: children ? 'Move or delete the tags under it first.' : undefined,
				onClick: () => remove(tag),
			},
		];
	};

	const renderRow = (tag, { handle } = {}) => {
		const menu = writable ? buildActionMenu(rowActions(tag)) : null;
		return (
			<div className="tag-tree__row" style={{ '--tag-depth': tag.depth || 1 }} data-testid="tag-row" data-tag-id={tagIdOf(tag)}>
				{handle || null}
				<div className="tag-tree__main">
					{/* The tag opens its tag page (T5): everything carrying it. */}
					<span className="tag-tree__head">
						<TagBadge tag={tag} size="sm" to={tagPagePath(tag) || undefined} automatic={rulesOn && isAutomaticTag(tag)} />
						{rulesOn && isPausedTag(tag) ? (
							<Tag className="tag-tree__paused" title="Its rule is paused: the rule puts nothing on and takes nothing off until it is resumed.">Paused</Tag>
						) : null}
					</span>
					{tag.description ? <span className="tag-tree__description" title={tag.description}>{tag.description}</span> : null}
					{newItemsLine(tag) ? <span className="tag-tree__description tag-tree__new-items" data-testid="tag-new-items">{newItemsLine(tag)}</span> : null}
					{passes[tagIdOf(tag)] ? (
						<TagRulePass tagId={tagIdOf(tag)} pass={passes[tagIdOf(tag)]} onDismiss={() => dropPass(tagIdOf(tag))} />
					) : null}
				</div>
				{menu ? (
					<span className="tag-tree__row-actions">
						<Dropdown menu={menu} trigger={['click']} placement="bottomRight">
							<Button type="text" icon={<KEBAB />} aria-label={`Actions for ${tag.name}`} />
						</Dropdown>
					</span>
				) : null}
			</div>
		);
	};

	// ---- the page --------------------------------------------------------

	const count = ordered.length;
	const atLimit = count >= TAG_LIMITS.perOwner;
	const items = ordered.map(tag => ({ id: tagIdOf(tag), tag }));

	return (
		<div className="tag-manager" data-testid="tag-manager">
			{readOnlyNote ? <Alert type="info" showIcon className="tag-manager__refusal" message={readOnlyNote} /> : null}
			{refusal ? (
				<Alert
					type="error"
					showIcon
					closable
					onClose={() => setRefusal(null)}
					className="tag-manager__refusal"
					message={refusal}
					data-testid="tag-refusal"
				/>
			) : null}
			{error && count ? <RefreshFailedNotice label="tags" onRetry={onRetry} /> : null}

			{writable ? (
				<div className="tag-manager__actions">
					<Button
						type={addButtonType}
						icon={<PLUS />}
						onClick={() => setEditing({ parentId: null })}
						disabled={atLimit}
						title={atLimit ? `This list already has ${TAG_LIMITS.perOwner} tags, the most it can hold.` : undefined}
					>
						Add tag
					</Button>
				</div>
			) : null}

			<GroupHeader
				label={label}
				count={count}
				action={writable && count > 1 ? (
					<ReorderButton active={reordering} what={privateTags ? 'private tags' : 'tags'} onClick={() => setReordering(r => !r)} />
				) : null}
			/>
			{!count && (loading || error) ? <TabLoadState loading={loading} error={error} label="tags" onRetry={onRetry} rows={3} /> : null}
			{!loading && !count && !error ? <p className="tag-manager__empty">{emptyText}</p> : null}
			{count ? (
				writable ? (
					<SortableList
						items={items}
						getLabel={row => row.tag.name}
						onReorder={handleReorder}
						renderItem={(row, props) => renderRow(row.tag, props)}
						reordering={reordering}
						className="tag-tree"
						ariaLabel={label}
					/>
				) : (
					<ul className="tag-tree" aria-label={label}>
						{ordered.map(tag => <li key={tagIdOf(tag)}>{renderRow(tag)}</li>)}
					</ul>
				)
			) : null}
			{reordering && phone ? (
				<p className="meta-line tag-manager__note">Drag a grip, or use Move to… in a tag’s ⋯ to put it under another tag.</p>
			) : null}

			{writable ? (
				<div className="tag-manager__deleted">
					<GroupHeader
						label="Deleted tags"
						collapsible
						collapsed={!deletedOpen}
						onToggle={toggleDeleted}
						count={deleted.loaded ? deleted.tags.length : undefined}
					/>
					{deletedOpen ? (
						deleted.loading && !deleted.loaded ? <Skeleton variant="list-row" count={2} /> : (
							deleted.error ? (
								<TabLoadState loading={false} error={deleted.error} label="deleted tags" onRetry={loadDeleted} />
							) : deleted.tags.length ? (
								<ul className="tag-tree" aria-label="Deleted tags">
									{deleted.tags.map(tag => (
										<li key={tagIdOf(tag)}>
											<div className="tag-tree__row" data-testid="deleted-tag-row">
												<div className="tag-tree__main">
													<TagBadge tag={tag} size="sm" />
													<span className="tag-tree__description">
														{tag.deletedAt ? `Deleted ${formatRelativeToNow(tag.deletedAt)}` : 'Deleted'}
													</span>
												</div>
												<span className="tag-tree__row-actions">
													<Button size="small" onClick={() => restore(tag)} aria-label={`Restore ${tag.name}`}>Restore</Button>
												</span>
											</div>
										</li>
									))}
								</ul>
							) : <p className="tag-manager__empty">No deleted tags.</p>
						)
					) : null}
				</div>
			) : null}

			<TagEditModal
				open={Boolean(editing)}
				onClose={() => setEditing(null)}
				onSave={save}
				onCheck={typeof service?.check === 'function' ? (fields, { tag }) => service.check(tagIdOf(tag), fields) : null}
				tag={editing?.tag || null}
				parentId={editing?.parentId || null}
				tags={tags}
				allowRestricted={allowRestricted}
				privateTags={privateTags}
				newItemCards={newItemCards}
			/>
			{merging ? (
				<TagMergeModal
					open={Boolean(merging)}
					onClose={() => setMerging(null)}
					tag={merging}
					tags={tags}
					service={service}
					onMerged={merged}
					privateTags={privateTags}
				/>
			) : null}
			{rulesOn && ruleFor ? (
				<TagRuleEditor
					open
					tag={tags.find(t => tagIdOf(t) === tagIdOf(ruleFor)) || ruleFor}
					collection={rules.collection || null}
					onClose={() => setRuleFor(null)}
					onSaved={ruleSaved}
					onPass={trackPass}
					onEnabled={ruleSwitched}
					onStop={stopRule}
				/>
			) : null}
			<TagMoveModal
				open={Boolean(moving)}
				onClose={() => setMoving(null)}
				onMove={moveTo}
				tag={moving}
				tags={tags}
			/>
		</div>
	);
}
